import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { adoptCompanyCurrency } from '@/lib/currency'
import { adoptCompanyTimeZone } from '@/lib/timezone'
import { adoptCompanyFiveSTarget } from '@/lib/fiveSTarget'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { deviceName } from '@/lib/device'
import {
  api,
  beginImpersonation,
  clearToken,
  endImpersonation,
  getToken,
  isImpersonating,
  setToken,
} from '@/lib/api'
import { clearRememberedRoute } from '@/lib/lastRoute'
import { disconnectRealtime } from '@/lib/realtime'
import type { Impersonation, User } from '@/types/api'

interface AuthState {
  user: User | null
  isLoading: boolean
  /**
   * Set while support is signed in as this user, null otherwise.
   *
   * Comes from the server on every `/auth/me`, and the customer's own user sees
   * it as well as the owner. That is the point: they are entitled to know, on
   * their own screen, that somebody else is signed in as them.
   */
  impersonation: Impersonation | null
  /**
   * Sign in as one of a customer's users. Owner only; the server enforces it.
   *
   * The reason is required and lands in that company's log, where their own
   * administrator can read it — see `isValidImpersonationReason`.
   */
  startImpersonation: (userId: string, reason: string) => Promise<User>
  /** Leave the support session and return to the owner's own. */
  stopImpersonation: () => Promise<void>
  /**
   * `remember: false` keeps the session in sessionStorage — see setToken.
   *
   * Resolves to a challenge instead of a session when the account has a second
   * factor: no token has been issued yet, and `completeTwoFactor` finishes it.
   */
  login: (
    employeeCode: string,
    password: string,
    remember?: boolean,
  ) => Promise<{ twoFactorRequired: true; challenge: string } | null>
  completeTwoFactor: (
    challenge: string,
    input: { code?: string; recoveryCode?: string },
    remember?: boolean,
  ) => Promise<void>
  /** Sign in by scanning the QR on a printed worker badge. */
  loginWithBadge: (
    token: string,
    remember?: boolean,
  ) => Promise<{ twoFactorRequired: true; challenge: string } | null>
  /**
   * Replace the activation code an administrator read out with a password of
   * one's own. Until this happens the API refuses everything else — see
   * EnsurePasswordIsSet on the server.
   */
  setOwnPassword: (password: string) => Promise<void>
  logout: () => Promise<void>
  /** Re-read `/auth/me`, for a company-level change the session carries. */
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null)

  /**
   * Setting the user also settles what money is counted in.
   *
   * Wrapped rather than called beside each of the eight `setUser` sites — sign
   * in, restore, activate, set password, verify a second factor, start and end
   * a support session, sign out — because a step one of them forgets is a
   * screen quietly printing the wrong currency, with nothing to show for it.
   *
   * Sign-out passes null and returns to the default, which matters on the
   * shared terminal by the door: the next person's sign-in screen must not
   * carry the last person's company with it. Starting a support session passes
   * the impersonated user, so an owner looking at a Croatian customer reads
   * euros for as long as they are in there.
   *
   * The company's time zone rides the same path, for the same reasons — see
   * lib/timezone.ts. It is set before the state, so the render this triggers
   * already prints its dates on the new clock. So does the 5S target, which
   * every score colour reads — see lib/fiveSTarget.ts.
   */
  const setUser = useCallback((next: User | null) => {
    adoptCompanyCurrency(next?.company?.currency)
    adoptCompanyTimeZone(next?.company?.timezone)
    adoptCompanyFiveSTarget(next?.company?.fiveSTarget)
    setUserState(next)
  }, [])
  const [impersonation, setImpersonation] = useState<Impersonation | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const queryClient = useQueryClient()

  // Restore the session on boot. A token that the server rejects is discarded
  // rather than left to fail every subsequent request.
  useEffect(() => {
    if (!getToken()) {
      setIsLoading(false)
      return
    }

    api
      .get<{ user: User; impersonation: Impersonation | null }>('/auth/me')
      .then(({ data }) => {
        setUser(data.user)
        // Restored from the response, not from storage: a reload during a
        // support session must bring the banner back with it.
        setImpersonation(data.impersonation ?? null)
      })
      .catch(() => clearToken())
      .finally(() => setIsLoading(false))
  }, [setUser])

  const login = useCallback(
    async (employeeCode: string, password: string, remember = true) => {
      const { data } = await api.post<{
        token?: string
        user?: User
        twoFactorRequired?: boolean
        challenge?: string
      }>('/auth/login', {
        employeeCode,
        password,
        deviceName: deviceName(),
        remember,
      })

      // With 2FA on, the server withholds the token and returns a short-lived
      // challenge instead. Nothing is stored until the code is proven.
      if (data.twoFactorRequired && data.challenge) {
        return { twoFactorRequired: true as const, challenge: data.challenge }
      }

      setToken(data.token!, remember)
      setUser(data.user!)

      return null
    },
    [setUser],
  )

  const loginWithBadge = useCallback(async (token: string, remember = true) => {
    const { data } = await api.post<{
      token?: string
      user?: User
      twoFactorRequired?: boolean
      challenge?: string
    }>('/auth/qr', { token, deviceName: deviceName(), remember })

    // A badge is one factor; an account with a second still has to clear it.
    if (data.twoFactorRequired && data.challenge) {
      return { twoFactorRequired: true as const, challenge: data.challenge }
    }

    setToken(data.token!, remember)
    setUser(data.user!)

    return null
  }, [setUser])

  const setOwnPassword = useCallback(async (password: string) => {
    const { data } = await api.post<{ user: User }>('/auth/set-password', {
      password,
      password_confirmation: password,
    })

    // Updating the user here is what lifts the gate in the UI — the flag it
    // keys on lives on the session object.
    setUser(data.user)
  }, [setUser])

  const completeTwoFactor = useCallback(
    async (
      challenge: string,
      input: { code?: string; recoveryCode?: string },
      remember = true,
    ) => {
      const { data } = await api.post<{ token: string; user: User }>(
        '/auth/two-factor-challenge',
        { challenge, ...input },
      )

      setToken(data.token, remember)
      setUser(data.user)
    },
    [setUser],
  )

  /**
   * Sign in as one of a customer's users.
   *
   * The cache is cleared on both edges. Going in, whatever the owner panel had
   * loaded is about a different company entirely; coming back, the screen is
   * full of one customer's submissions. Leaving either in place would show one
   * account's data under another account's name — the same failure the shared
   * shop-floor terminals get cleared for.
   */
  const startImpersonation = useCallback(
    async (userId: string, reason: string) => {
      const { data } = await api.post<{
        token: string
        user: User
        expiresAt: string | null
        impersonatedBy: { id: string; name: string }
      }>(`/owner/impersonate/${userId}`, { reason: reason.trim() })

      // Swaps tokens without touching the owner's own remembered session —
      // see beginImpersonation for why this cannot go through setToken.
      beginImpersonation(data.token)

      queryClient.clear()
      disconnectRealtime()

      setUser(data.user)
      setImpersonation({
        ownerId: data.impersonatedBy.id,
        ownerName: data.impersonatedBy.name,
        expiresAt: data.expiresAt,
      })

      return data.user
    },
    [queryClient, setUser],
  )

  const stopImpersonation = useCallback(async () => {
    try {
      await api.post('/auth/stop-impersonating')
    } catch {
      /*
       * The way out must not be blocked by the request that announces it.
       *
       * If the support token has already expired the server will refuse, and
       * refusing to restore the owner's own session over that would strand them
       * in a dead account with no control that works. The token is revoked
       * server-side by its own expiry either way.
       */
    } finally {
      endImpersonation()
      queryClient.clear()
      disconnectRealtime()
      setImpersonation(null)

      const { data } = await api.get<{ user: User }>('/auth/me')
      setUser(data.user)
    }
  }, [queryClient, setUser])

  /**
   * Re-read the session from the server.
   *
   * For a change somebody makes to their *company* rather than to themselves —
   * the currency is the first — where the consequence is carried on the session
   * object and every screen reads it from there. Without this the administrator
   * saves, is told it worked, and goes on seeing the old symbol on every amount
   * until they reload; which reads as the setting not working.
   *
   * Goes through `setUser`, so the currency is adopted by the same path as a
   * sign-in rather than by a second mechanism that can drift from it.
   */
  const refreshUser = useCallback(async () => {
    const { data } = await api.get<{ user: User }>('/auth/me')

    setUser(data.user)
  }, [setUser])

  const logout = useCallback(async () => {
    /*
     * Signing out of a support session means leaving it, not signing out.
     *
     * `clearToken()` below wipes both stores, so without this the owner
     * pressing the shell's normal sign-out while impersonating would destroy
     * their **own** remembered session as collateral — and log the customer's
     * user out of the application for good measure.
     */
    if (isImpersonating()) {
      await stopImpersonation()

      return
    }

    try {
      await api.post('/auth/logout')
    } catch {
      // A failed logout call must not trap the user in the app — the local
      // token is cleared either way.
    } finally {
      clearToken()
      // A deliberate sign-out ends the trail too — the next person to use this
      // device must not be sent to the last screen its previous owner had open.
      clearRememberedRoute()
      /*
       * And close the socket. It was authorised with the token just discarded,
       * and on a shared shop-floor terminal an open connection would keep
       * pushing the previous person's channels into the next person's browser.
       */
      disconnectRealtime()
      setUser(null)
      setImpersonation(null)
      // Wipe the cache so the next person to log in on a shared plant tablet
      // cannot see the previous user's submissions.
      queryClient.clear()
    }
  }, [queryClient, stopImpersonation, setUser])

  const value = useMemo(
    () => ({
      user,
      isLoading,
      impersonation,
      login,
      completeTwoFactor,
      loginWithBadge,
      setOwnPassword,
      startImpersonation,
      stopImpersonation,
      refreshUser,
      logout,
    }),
    [
      user,
      isLoading,
      impersonation,
      login,
      completeTwoFactor,
      loginWithBadge,
      setOwnPassword,
      startImpersonation,
      stopImpersonation,
      refreshUser,
      logout,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}
